import {
	customerTokenKey, customerSessionKey, loopTokenKey, subscriptionPageUrl,
	STORE_ID, REDIRECT_URL, CLIENT_ID, CURRENT_COUNTRY
} from "utils/constants";
import { LOGOUT } from "utils/action-types";
import getStorageValue from "helpers/get-storage-value";
import {
	createCodeVerifier, createCodeChallenge, setCustomerAccessToken, getCustomerAccessToken, isValidToken
} from "utils/oauth";
import { getCustomerProfile } from "actions/account/profile";
import navigate from "helpers/navigate";
import axios from "axios";
import { trackLogin } from "services/analytics";

const getRedirectUrl = () => {
	const language = window.__LANGUAGE;
	return language === "default" ? REDIRECT_URL : `${REDIRECT_URL}/${language}`;
};

const axiosInstance = axios.create({
	baseURL: `https://shopify.com/${STORE_ID}/auth/oauth/token`,
	headers: { "Content-Type": "application/x-www-form-urlencoded" },
});

const toQueryString = ({ ...params }) => new URLSearchParams(params).toString();

export const getOrRefreshCustomerToken = async () => {
	const token = getCustomerAccessToken();

	if (token?.access_token)
		return isValidToken(token) ? token.access_token : await refreshCustomerToken();

	return null;
};

const axiosRequest = async (data) => {
	try {
		const response = await axiosInstance.post("", data);
		return response.data;
	} catch (error) {
		console.error("Request error:", error);
		return error.response.data;
	}
};

export const shopifyLogin = async () => {
	const { challenge } = await retrieveCodeVerifier();
	const urlParams = new URLSearchParams({
		client_id: CLIENT_ID,
		response_type: "code",
		redirect_uri: getRedirectUrl() + subscriptionPageUrl,
		code_challenge_method: "S256",
		scope: "openid email https://api.customers.com/auth/customer.graphql",
		code_challenge: challenge,
	});
	if (window.__LANGUAGE === "default")
		urlParams.append("ui_locales", CURRENT_COUNTRY);

	const authURL = `https://shopify.com/${STORE_ID}/auth/oauth/authorize?${urlParams.toString()}`;
	window.location.href = authURL;
};

const retrieveCodeVerifier = async () => {
	let code_verifier = localStorage.getItem("verifier");
	let challenge = localStorage.getItem("challenge");

	if (!code_verifier || !challenge) {
		code_verifier = createCodeVerifier();
		challenge = await createCodeChallenge(code_verifier);

		localStorage.setItem("verifier", code_verifier);
		localStorage.setItem("challenge", challenge);
	}
	return { code_verifier, challenge };
};

const obtainAccessToken = async (code_verifier, code) => {
	const requestData = toQueryString({
		grant_type: "authorization_code",
		client_id: CLIENT_ID,
		code,
		redirect_uri: getRedirectUrl() + subscriptionPageUrl,
		code_verifier,
	});

	return axiosRequest(requestData);
};

export const exchangeToken = async (subject_token) => {
	const exchangeData = toQueryString({
		grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
		client_id: CLIENT_ID,
		audience: "30243aa5-17c1-465a-8493-944bcc4e88aa",
		subject_token,
		scopes: "https://api.customers.com/auth/customer.graphql",
		subject_token_type: "urn:ietf:params:oauth:token-type:access_token",
	});

	return axiosRequest(exchangeData);
};

export const authenticateCustomer = () => async (dispatch) => {
	// Retrieve or generate verifier and challenge
	const { code_verifier } = await retrieveCodeVerifier();

	// Check for authorization code in current URL
	const url = new URL(window.location);
	const code = url.searchParams.get("code");

	const tokenResponse = await obtainAccessToken(code_verifier, code);
	if (tokenResponse?.error)
		return navigate("/account/login");

	if (tokenResponse) {
		const { access_token, refresh_token, id_token, expires_in } = tokenResponse;
		const token = await exchangeToken(access_token);

		// Clear stored verifier and challenge after successful exchange
		localStorage.removeItem("verifier");
		localStorage.removeItem("challenge");
		const customerToken = {
			...token,
			expires_at: new Date(new Date().getTime() + expires_in * 1000).toString(),
			refresh_token,
			id_token,
		};

		url.searchParams.delete("code");
		window.history.replaceState({}, "", url);
		setCustomerAccessToken(customerToken);
		const profile = await dispatch(getCustomerProfile());

		if (profile && profile.emailAddress) {
			const email = profile.emailAddress.emailAddress;

			const { firstName, lastName, id } = profile;
			trackLogin({ firstName, lastName, id, email });
		}
	}
};

export const refreshCustomerToken = async () => {
	const refreshToken = getStorageValue(customerTokenKey, "refresh_token", "local");
	if (!refreshToken) return null;

	const body = new URLSearchParams();
	body.append("grant_type", "refresh_token");
	body.append("client_id", CLIENT_ID);
	body.append("refresh_token", refreshToken);

	const headers = { "Content-Type": "application/x-www-form-urlencoded" };

	try {
		const response = await axios.post(`https://shopify.com/${STORE_ID}/auth/oauth/token`, body, { headers });
		const refreshedToken = response.data;
		if (refreshedToken) {
			const exchangedToken = await exchangeToken(refreshedToken.access_token);

			const currentToken = getCustomerAccessToken();

			setCustomerAccessToken({
				...currentToken,
				...exchangedToken,
				expires_at: new Date(new Date().getTime() + exchangedToken.expires_in * 1000).toString()
			});

			return exchangedToken.access_token;
		}

		return null;
	} catch (error) {
		console.error("Error refreshing token:", error);
		return null;
	}
};

export const logOut = () => async (dispatch) => {
	const logOutUrl = `https://shopify.com/${STORE_ID}/auth/logout?id_token_hint=${getStorageValue(
		customerTokenKey,
		"id_token",
		"local",
	)}&post_logout_redirect_uri=${getRedirectUrl()}`;

	window.localStorage.removeItem(customerTokenKey);
	window.sessionStorage.removeItem(customerSessionKey);
	window.localStorage.removeItem(loopTokenKey);

	dispatch({ type: LOGOUT });
	window.location.href = logOutUrl;
};
